import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Pause, Play, Trash2, User, Mail, FileText, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MonComptePage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [myAds, setMyAds] = useState([]);
  const [sentProposals, setSentProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profil');

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      if (response.data.user_type === 'acquereur' || response.data.user_type === 'both') {
        await fetchMyAds();
      }
      if (response.data.user_type === 'vendeur' || response.data.user_type === 'both') {
        await fetchSentProposals();
      }
    } catch (error) {
      toast.error('Connexion requise');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyAds = async () => {
    try {
      const response = await axios.get(`${API}/acquirer-ads/my`, { withCredentials: true });
      setMyAds(response.data);
    } catch (error) { console.error(error); }
  };

  const fetchSentProposals = async () => {
    try {
      const response = await axios.get(`${API}/proposals/sent`, { withCredentials: true });
      setSentProposals(response.data);
    } catch (error) { console.error(error); }
  };

  const toggleStatus = async (adId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'paused' : 'active';
      await axios.patch(`${API}/acquirer-ads/${adId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success(newStatus === 'active' ? 'Réactivée' : 'En pause');
      fetchMyAds();
    } catch (error) { toast.error('Erreur'); }
  };

  const deleteAd = async (adId) => {
    if (!confirm('Supprimer ?')) return;
    try {
      await axios.delete(`${API}/acquirer-ads/${adId}`, { withCredentials: true });
      toast.success('Supprimée');
      fetchMyAds();
    } catch (error) { toast.error('Erreur'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  const isAcquereur = user?.user_type === 'acquereur' || user?.user_type === 'both';
  const isVendeur = user?.user_type === 'vendeur' || user?.user_type === 'both';

  return (
    <div className="min-h-screen bg-background">
      <Header
        currentUser={user}
        acquirerAdsCount={0}
        onLoginClick={() => {}}
        onAboutClick={() => {}}
        onCreateAdClick={() => navigate('/create-search')}
        onLogout={async () => {
          await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
          navigate('/');
          window.location.reload();
        }}
      />

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-border p-8">
          <h2 className="font-display text-3xl font-bold text-foreground mb-8">Mon Compte</h2>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full bg-muted/50" style={{ gridTemplateColumns: isAcquereur && isVendeur ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)' }}>
              <TabsTrigger value="profil" className="font-heading">
                <User className="w-4 h-4 mr-2" />
                Profil
              </TabsTrigger>
              {isAcquereur && (
                <TabsTrigger value="mes-annonces" className="font-heading">
                  <FileText className="w-4 h-4 mr-2" />
                  Annonces ({myAds.length})
                </TabsTrigger>
              )}
              {isVendeur && (
                <TabsTrigger value="mes-propositions" className="font-heading">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Propositions ({sentProposals.length})
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="profil" className="mt-6">
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-blue-600" />
                    <p className="text-sm text-gray-600 font-semibold">Nom</p>
                  </div>
                  <p className="text-lg font-bold text-gray-800">{user?.name}</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Mail className="w-4 h-4 text-purple-600" />
                    <p className="text-sm text-gray-600 font-semibold">Email</p>
                  </div>
                  <p className="text-lg font-bold text-gray-800">{user?.email}</p>
                </div>
                <div className="bg-muted/50 p-6 rounded">
                  <p className="text-sm text-muted-foreground font-semibold mb-2">Type de compte</p>
                  <p className="text-lg font-bold text-foreground capitalize">
                    {user?.user_type === 'acquereur' ? 'Acquéreur' : user?.user_type === 'vendeur' ? 'Vendeur' : 'Les deux'}
                  </p>
                </div>
                
                <div className="pt-6 border-t border-border">
                  <Button
                    onClick={async () => {
                      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
                      navigate('/');
                      window.location.reload();
                    }}
                    variant="destructive"
                    className="w-full py-3"
                  >
                    Se déconnecter
                  </Button>
                </div>
              </div>
            </TabsContent>

            {isAcquereur && (
              <TabsContent value="mes-annonces" className="mt-6">
                {myAds.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-600 mb-3 text-sm">Aucune annonce</p>
                    <Button onClick={() => navigate('/create-search')} className="rounded-full" size="sm">➕ Créer</Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {myAds.map((ad) => (
                      <div 
                        key={ad.ad_id} 
                        className="bg-white border rounded-xl p-3 cursor-pointer hover:shadow-lg transition-shadow"
                        onClick={() => navigate(`/my-ad/${ad.ad_id}`)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-bold text-sm text-gray-800 mb-1">{ad.title}</h3>
                            <div className="flex gap-1 mb-1">
                              <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs">{ad.property_type}</span>
                              <span className={`px-2 py-0.5 rounded-full text-xs ${
                                ad.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {ad.status === 'active' ? '✓' : '⏸'}
                              </span>
                            </div>
                            <p className="text-xs text-gray-600">💬 {ad.proposals_received} proposition(s)</p>
                            <p className="text-xs text-blue-600 font-semibold mt-1">→ Cliquez pour voir les propositions</p>
                          </div>
                          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button onClick={() => toggleStatus(ad.ad_id, ad.status)} size="sm" variant="outline" className="rounded-full p-2">
                              {ad.status === 'active' ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                            </Button>
                            <Button onClick={() => deleteAd(ad.ad_id)} size="sm" variant="destructive" className="rounded-full p-2">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            )}

            {isVendeur && (
              <TabsContent value="mes-propositions" className="mt-6">
                {sentProposals.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-600 mb-3 text-sm">Aucune proposition</p>
                    <Button onClick={() => navigate('/')} className="rounded-full" size="sm">🔍 Chercher</Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {sentProposals.map((prop) => (
                      <div key={prop.proposal_id} className="bg-white border rounded-xl p-3 cursor-pointer hover:shadow-lg transition-shadow"
                        onClick={() => navigate(`/proposal/${prop.proposal_id}`)}>
                        <h3 className="font-bold text-sm text-gray-800 mb-1">{prop.property_address}</h3>
                        <p className="text-base font-bold text-blue-600">💰 {prop.price.toLocaleString('fr-FR')} €</p>
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs mt-1 ${
                          prop.status === 'sent' ? 'bg-yellow-100 text-yellow-800' :
                          prop.status === 'interested' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {prop.status === 'sent' ? '📨' : prop.status === 'interested' ? '✅' : '❌'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            )}

          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default MonComptePage;
